import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

const variants = {
  variant: {
    default: "bg-primary text-primary-foreground shadow hover:bg-primary/90",
    destructive:
      "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
    outline:
      "border border-primary-400 bg-primary-background-100 shadow-sm hover:bg-primary-background-500 text-primary-400 hover:text-primary-500",
    secondary:
      "bg-secondary-50 text-secondary-500 shadow-sm hover:bg-secondary-100/80",
    ghost: "hover:bg-accent hover:text-accent-foreground",
    link: "text-primary underline-offset-4 hover:underline",
    success: "bg-success-100 text-success-900 hover:bg-success-200/80",
    primary: "bg-primary-50 text-primary-400 hover:bg-primary-100/90",
  },
  size: {
    default: "h-9 px-4 py-2",
    xs: "h-8 px-3 text-xs",
    sm: "h-8  px-3 text-xs",
    lg: "h-10  px-8",
    xl: "h-12 px-8",
    icon: "h-9 w-9",
  },
  radius: {
    default: "rounded-md",
    sm: "rounded-sm",
    lg: "rounded-lg",
    full: "rounded-full",
  },
};

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-normal transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 cursor-pointer",
  {
    variants,
    defaultVariants: {
      variant: "default",
      size: "default",
      radius: "default",
    },
  },
);


export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, radius, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, radius, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants, variants };
